import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { staffKeys } from "@/modules/staff/hooks/useStaff";
import { faceEnrollmentService } from "../services/face-enrollment.service";
import type {
	CreateFaceEnrollmentRequest,
	ErrorResponse,
	ListFaceEnrollmentParams,
	UpdateFaceEnrollmentRequest,
} from "../types";

export const faceEnrollmentKeys = {
	all: ["face-enrollments"] as const,
	lists: () => [...faceEnrollmentKeys.all, "list"] as const,
	list: (params: ListFaceEnrollmentParams) => [...faceEnrollmentKeys.lists(), params] as const,
	details: () => [...faceEnrollmentKeys.all, "detail"] as const,
	detail: (id: string) => [...faceEnrollmentKeys.details(), id] as const,
};

export function useFaceEnrollmentList(params: ListFaceEnrollmentParams) {
	return useQuery({
		queryKey: faceEnrollmentKeys.list(params),
		queryFn: () => faceEnrollmentService.list(params),
		placeholderData: (previousData) => previousData,
	});
}

export function useFaceEnrollment(id: string) {
	return useQuery({
		queryKey: faceEnrollmentKeys.detail(id),
		queryFn: () => faceEnrollmentService.getById(id),
		enabled: !!id,
	});
}

export function useCreateFaceEnrollment() {
	const queryClient = useQueryClient();
	const { message } = App.useApp();

	return useMutation({
		mutationFn: (data: CreateFaceEnrollmentRequest) => faceEnrollmentService.create(data),
		onSuccess: (created) => {
			message.success("Yuz ma'lumotlari ro'yxatdan o'tkazildi");
			queryClient.invalidateQueries({ queryKey: faceEnrollmentKeys.lists() });
			// The enrollment photo now doubles as the staff's profile picture.
			queryClient.invalidateQueries({ queryKey: staffKeys.lists() });
			if (created.staffId) {
				queryClient.invalidateQueries({ queryKey: staffKeys.detail(created.staffId) });
			}
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Xatolik yuz berdi";
			message.error(msg);
		},
	});
}

export function useUpdateFaceEnrollment() {
	const queryClient = useQueryClient();
	const { message } = App.useApp();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateFaceEnrollmentRequest }) =>
			faceEnrollmentService.update(id, data),
		onSuccess: (updated, variables) => {
			message.success("Yuz ma'lumotlari yangilandi");
			queryClient.invalidateQueries({ queryKey: faceEnrollmentKeys.lists() });
			queryClient.invalidateQueries({ queryKey: faceEnrollmentKeys.detail(variables.id) });
			// The enrollment photo now doubles as the staff's profile picture.
			queryClient.invalidateQueries({ queryKey: staffKeys.lists() });
			if (updated.staffId) {
				queryClient.invalidateQueries({ queryKey: staffKeys.detail(updated.staffId) });
			}
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Xatolik yuz berdi";
			message.error(msg);
		},
	});
}

export function useDeleteFaceEnrollment() {
	const queryClient = useQueryClient();
	const { message } = App.useApp();

	return useMutation({
		mutationFn: (id: string) => faceEnrollmentService.delete(id),
		onSuccess: () => {
			message.success("Yuz ma'lumotlari o'chirildi");
			queryClient.invalidateQueries({ queryKey: faceEnrollmentKeys.lists() });
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "O'chirishda xatolik yuz berdi";
			message.error(msg);
		},
	});
}

export function useSyncFaceEnrollment() {
	const { message } = App.useApp();

	return useMutation({
		mutationFn: (faceEnrollmentId: string) => faceEnrollmentService.sync(faceEnrollmentId),
		onSuccess: (data) => {
			if (data.success) {
				message.success(data.message);
			} else {
				message.warning(data.message);
			}
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Sinxronlashtirishda xatolik yuz berdi";
			message.error(msg);
		},
	});
}

export function useSyncFaceEnrollmentsBulk() {
	const { message } = App.useApp();

	return useMutation({
		mutationFn: (faceEnrollmentIds: string[]) => faceEnrollmentService.syncBulk(faceEnrollmentIds),
		onSuccess: (data) => {
			message.info(
				`Sinxronlashtirish yakunlandi: ${data.succeeded} muvaffaqiyatli, ${data.failed} xatolik`
			);
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg =
				error.response?.data?.error?.message || "Bulk sinxronlashtirishda xatolik yuz berdi";
			message.error(msg);
		},
	});
}
